import { Avatar } from './avatar';

import { Bubble } from './bubble';
import matchUrl from 'match-url-wildcard';
import {
  Configurations,
  ChatTranslations,
  MetaEntry,
  CustomConfig,
  DefaultLanguage,
  ChatbotType,
  FloatingIconStyle,
} from './types';
import _ from 'lodash';
import {
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  META_STORAGE_NAME,
} from './placeholder.constants';
import { throwError } from 'rxjs';
import { nanoid } from 'nanoid';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const defaultCss = require('./placeholder.css');

function makeRequest(
  url: URL,
  group = '',
  method?: string,
  data?: any,
  headers?: Record<string, string>,
): Promise<string> {
  const request = new XMLHttpRequest();

  return new Promise(function (resolve, reject) {
    request.onreadystatechange = function (): void {
      if (request.readyState !== 4) {
        return;
      }
      if (request.status >= 200 && request.status < 300) {
        resolve(request.responseText);
      } else {
        reject({
          status: request.status,
          statusText: request.statusText,
        });
      }
    };

    request.onerror = function (): void {
      reject({
        status: 0,
        statusText: 'Network error or CORS blocked',
      });
    };

    request.open(method || 'GET', url.toString(), true);
    const token = localStorage.getItem(`c_at_${group}`);

    if (token) {
      request.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    if (data) {
      request.setRequestHeader(
        'Content-Type',
        'Application/json; charset=utf-8',
      );
    }

    if (headers) {
      Object.entries(headers).forEach(([key, value]) => {
        request.setRequestHeader(key, value);
      });
    }
    data ? request.send(JSON.stringify(data)) : request.send();
  });
}

// A whitelist entry without a protocol (e.g. "www.something.de" instead of
// "https://www.something.de") can still pass matchUrl's wildcard matching,
// but breaks downstream logic that expects an absolute URL, so it must be
// excluded from the whitelist before matching.
const isAbsoluteUrl = (url: string): boolean => /^https?:\/\//i.test(url.trim());

const VIDEO_URL_EXTENSIONS = ['.mp4', '.webm', '.mov'];

function isVideoUrl(url: string): boolean {
  const pathname = url.split(/[?#]/)[0].toLowerCase();
  return VIDEO_URL_EXTENSIONS.some((extension) => pathname.endsWith(extension));
}

function setupBubble(
  config: Configurations,
  baseUrl: URL,
  embedUrl: URL,
): void {
  // We are using a shadow root so that there are no clashes between
  // the customer’s and our own CSS.
  const wrapper = document.createElement('div');
  wrapper.id = 'kauz-root';
  wrapper.attachShadow({ mode: 'open' });
  document.body.appendChild(wrapper);

  const defaultStyle = document.createElement('style');
  defaultStyle.type = 'text/css';
  defaultStyle.innerHTML = defaultCss;
  wrapper.shadowRoot.appendChild(defaultStyle);

  const styleUrl = new window.URL(config.userStyle.url, baseUrl);
  const link = document.createElement('link');
  link.setAttribute('rel', 'stylesheet');
  link.setAttribute('href', styleUrl.toString());

  const customStyleLoaded = new Promise((res, rej) => {
    link.onload = res;
    link.onerror = rej;
    wrapper.shadowRoot.appendChild(link);
  });

  const hasCustomFloatingIcon =
    config.type === ChatbotType.WEBSITE &&
    Boolean(config.placeholderFloatingIcon);
  const customFloatingIconIsVideo =
    hasCustomFloatingIcon && isVideoUrl(config.placeholderFloatingIcon);

  customStyleLoaded
    .catch(() => {
      console.log('failed to load user-supplied CSS');
    })
    .finally(() => {
      const avatar = new Avatar(config);
      const bubble = new Bubble({
        url: embedUrl.toString(),
        group: config.bot.group,
        keepChatOpen: config.websiteChatConfig?.keepChatOpen ?? false,
      });

      if (hasCustomFloatingIcon && customFloatingIconIsVideo) {
        const botAvatarDiv = avatar.element.querySelector('.bot.avatar');
        if (botAvatarDiv) {
          const video = document.createElement('video');
          video.src = config.placeholderFloatingIcon;
          video.autoplay = true;
          video.loop = true;
          video.muted = true;
          video.playsInline = true;
          video.style.width = '100%';
          video.style.height = '100%';
          video.style.objectFit = 'cover';
          video.style.borderRadius = '100%';
          botAvatarDiv.appendChild(video);
        }
      } else if (
        !hasCustomFloatingIcon &&
        config.type === ChatbotType.WEBSITE &&
        config.websiteChatConfig?.floatingIconStyle === FloatingIconStyle.DYNAMIC_AVATAR &&
        config.loopVideoLink && config.websiteChatConfig?.dynamicAvatarEnabled
      ) {
        const botAvatarDiv = avatar.element.querySelector('.bot.avatar');
        if (botAvatarDiv) {
          const video = document.createElement('video');
          video.src = config.loopVideoLink;
          video.autoplay = true;
          video.loop = true;
          video.muted = true;
          video.playsInline = true;
          video.style.width = '100%';
          video.style.height = '100%';
          video.style.objectFit = 'cover';
          video.style.borderRadius = '100%';
          botAvatarDiv.appendChild(video);
        }
      }

      wrapper.shadowRoot.appendChild(avatar.element);
      wrapper.shadowRoot.appendChild(bubble.element);

      bubble.autoOpenIfNeeded();
    });

  if (config.colorScheme) {
    const avatarURL = new window.URL(config.botAvatar, baseUrl);
    const floatingIconURL =
      hasCustomFloatingIcon && !customFloatingIconIsVideo
        ? new window.URL(config.placeholderFloatingIcon, baseUrl)
        : null;
    const style = document.createElement('style');
    style.appendChild(
      document.createTextNode(`
        #placeholder {
          ${
            config.colorScheme['mat-primary-500']
              ? `--mat-primary-500: ${config.colorScheme['mat-primary-500']};`
              : ''
          }
          ${
            config.colorScheme['mat-primary-contrast-500']
              ? `--mat-primary-contrast-500: ${config.colorScheme['mat-primary-contrast-500']};`
              : ''
          }
          ${
            config.colorScheme['mat-secondary-500']
              ? `--mat-secondary-500: ${config.colorScheme['mat-secondary-500']};`
              : ''
          }
          ${
            config.colorScheme['mat-secondary-contrast-500']
              ? `--mat-secondary-contrast-500: ${config.colorScheme['mat-secondary-contrast-500']};`
              : ''
          }
          ${
            config.colorScheme['chatbot-background-color']
              ? `--chatbot-background-color: ${config.colorScheme['chatbot-background-color']};`
              : ''
          }
          ${
            hasCustomFloatingIcon
              ? `.bot.avatar {
                    background-image: ${
                      customFloatingIconIsVideo ? 'none' : `url("${floatingIconURL}")`
                    };
                  }`
              : config.botAvatar &&
                  !(
                    config.type === ChatbotType.WEBSITE &&
                    config.websiteChatConfig?.floatingIconStyle ===
                      FloatingIconStyle.DYNAMIC_AVATAR &&
                    config.loopVideoLink && config.websiteChatConfig?.dynamicAvatarEnabled
                  )
                ? `.bot.avatar {
                    background-image: url("${avatarURL}");
                  }`
                : ''
          }
        }`),
    );
    wrapper.shadowRoot.appendChild(style);
  }

  if (config.customStylesExpanded) {
    const style = document.createElement('style');
    style.appendChild(
      document.createTextNode(config.customStylesExpanded),
    );
    wrapper.shadowRoot.appendChild(style);
  }
}

const checkAndGetLanguage = async (
  config: Configurations,
  defaultLanguagesUrl: URL,
): Promise<string> => {
  const defaultLanguageResponse = (await makeRequest(
    defaultLanguagesUrl,
    config.bot.group,
    'GET',
    null,
    {
      'kauz-request-origin': window.location.origin,
      'kauz-correlation-id': nanoid(14),
    },
  )) as string;
  const metaString = localStorage.getItem(
    `${META_STORAGE_NAME}_${config.bot.group}`,
  );
  const meta = metaString ? (JSON.parse(metaString) as MetaEntry[]) : [];
  const lngFromMeta = _.chain(meta)
    .find((i) => i.key === 'chat_i18n_language')
    .get('value')
    .value();
  let browserLanguage = navigator.language;
  browserLanguage = browserLanguage
    ? browserLanguage.split('-')[0]
    : browserLanguage;
  const defaultLanguage = (
    JSON.parse(defaultLanguageResponse) as DefaultLanguage
  ).code;
  const parentDocumentLanguage = window.document.documentElement.lang;
  console.log('Parent page language:', parentDocumentLanguage);
  const parentDocumentLanguageSanitized = _.chain(parentDocumentLanguage)
    .split('-')
    .head()
    .upperCase()
    .value();

  return (
    parentDocumentLanguageSanitized ||
    lngFromMeta ||
    defaultLanguage ||
    browserLanguage ||
    DEFAULT_LANGUAGE
  );
};

const getTranslations = async (
  config: Configurations,
  languagesServerUrl: URL,
): Promise<void> => {
  const attemptRequest = async (): Promise<void> => {
    try {
      const result: string = await makeRequest(
        languagesServerUrl,
        config.bot.group,
        'GET',
      );

      if (result) {
        const translations = JSON.parse(result) as ChatTranslations;
        config.placeholder.avatar.toast.message = _.get(
          translations,
          'kauzChatGreeting',
          'Hallo!',
        );
        const wrapper = document.getElementById('kauz-root');

        if (wrapper && wrapper.shadowRoot) {
          const greetingElement = wrapper.shadowRoot.getElementById('greeting');

          if (greetingElement) {
            greetingElement.innerHTML = config.placeholder.avatar.toast.message;
          }
        }
      } else {
        console.error('Language configuration problem.');
        throwError(() => new Error('Language configuration problem.'));
      }
    } catch (e) {
      if (
        languagesServerUrl.searchParams.get('languageCode').toUpperCase() ===
        FALLBACK_LANGUAGE
      ) {
        throwError(e);
      }

      languagesServerUrl.searchParams.set('languageCode', FALLBACK_LANGUAGE);
      return attemptRequest();
    }
  };

  return attemptRequest();
};

// The placeholder runs under the customer’s URL. In order to get the
// configuration file running at our own URL without any explicit configuration
// on the customer’s side, we rely on the source URL this script is loaded from.

function initChat(currentScript) {
  const src = new window.URL(currentScript, window.location.href);
  const baseUrl = new window.URL('..', src);
  const embedUrl = new window.URL('..', src);
  embedUrl.search = src.search;
  const configUrl = new window.URL('assets/config/config.json', baseUrl);
  let config = {} as Configurations;

  const requestConfig = makeRequest(configUrl);
  requestConfig
    .then((conf: string) => {
      config = JSON.parse(conf) as Configurations;

      const configServerUrl = new window.URL(
        `${config.bot.moonkin.url}/api/v4/chat/configurations`,
      );
      configServerUrl.search = src.search;
      const languagesServerUrl = new window.URL(
        `${config.bot.moonkin.url}/api/v4/chat/translations/one`,
      );
      languagesServerUrl.search = src.search;
      const defaultLanguagesUrl = new window.URL(
        `${config.bot.moonkin.url}/api/v4/chat/standard-languages/one`,
      );
      defaultLanguagesUrl.search = src.search;

      const group = configServerUrl.searchParams.get('group');
      config.bot.group = group;

      if (config.multiClientSupport && group) {
        // Get configuration for the group
        makeRequest(configServerUrl, group, 'GET', null, {
          'kauz-request-origin': window.location.origin,
          'kauz-correlation-id': nanoid(14),
        })
          .then(async (response: string) => {
            if (response) {
              const customConfig = JSON.parse(response) as CustomConfig;
              config.placeholder.avatar.toast = {
                message: '',
                placeholderTimeout:
                  customConfig.websiteChatConfig.placeholderGreetingDuration,
              };
              // custom styles
              config.type = customConfig.type === ChatbotType.PHONEBOT ? ChatbotType.WEBSITE : customConfig.type;
              config.loopVideoLink = customConfig.loopVideoLink;
              config.colorScheme = customConfig.type === ChatbotType.WEBSITE || customConfig.type === ChatbotType.PHONEBOT ? customConfig.websiteChatConfig.colorScheme : customConfig.avatarChatConfig.colorScheme;
              config.websiteChatConfig = customConfig.websiteChatConfig;
              config.avatarChatConfig = customConfig.avatarChatConfig;
              config.phonebotChatConfig = customConfig.phonebotChatConfig;
              config.customStylesExpanded = customConfig.customStyles;

              if (!customConfig.websiteChatConfig?.showGreetingOnce) {
                localStorage.removeItem(`c_greeting_shown_${group}`);
              }

              if (!customConfig.websiteChatConfig?.keepChatOpen) {
                localStorage.removeItem(`c_chat_open_${group}`);
              }
              config.botAvatar = customConfig.botAvatar;
              config.placeholderFloatingIcon =
                customConfig.placeholderFloatingIcon;
              config.supportedHosts = customConfig.kauzChatCorsOrigin
                .split(',')
                .filter(isAbsoluteUrl);

              // Get languages configuration
              const requiredLang = await checkAndGetLanguage(
                config,
                defaultLanguagesUrl,
              );
              languagesServerUrl.searchParams.set(
                'languageCode',
                requiredLang.toUpperCase(),
              );
              await getTranslations(config, languagesServerUrl);

              // Track language change on the parent page
              observeLangAttribute(config, languagesServerUrl);

              // Only setup Bubble if kauzChatCorsOrigin is allowing the page to setup bubble
              const windowURLIsAllowedByCustomCustomerCors = matchUrl(
                window.location.href,
                config.supportedHosts,
              );

              if (windowURLIsAllowedByCustomCustomerCors === false) {
                throw new Error(
                  'This page is not allowed to embed this placeholder script by Kauz AIStudio Whitelist settings!',
                );
              } else {
                setupBubble(config, baseUrl, embedUrl);
              }
            } else {
              console.error('Configuration problem.');
            }
          })
          .catch((e) => {
            console.error('Configuration problem.');
            console.error(e.message || e);
          });
      } else if (config.multiClientSupport && !group) {
        console.error('Configuration problem. Group have to be provided.');
      } else {
        setupBubble(config, baseUrl, embedUrl);
      }
    })
    .catch(console.log);
}

const observeLangAttribute = async (
  config: Configurations,
  languagesServerUrl: URL,
): Promise<void> => {
  // set logic for observing page language
  const targetNode = document.documentElement;
  const observerConfig = { attributes: true, attributeFilter: ['lang'] };
  const callback = async (mutationsList: any) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
        const requiredLang = _.chain(targetNode.getAttribute('lang'))
          .split('-')
          .head()
          .upperCase()
          .value();

        if (requiredLang) {
          languagesServerUrl.searchParams.set('languageCode', requiredLang);
          await getTranslations(config, languagesServerUrl);

          console.log(`Avatar lang: ${requiredLang}`);
        }
      }
    }
  };
  const observer = new MutationObserver(callback);
  observer.observe(targetNode, observerConfig);
  console.log('Observation of the lang attribute change for avatar started.');
};

try {
  const currentScript = document.currentScript.getAttribute('src');
  initChat(currentScript);
} catch (e) {
  console.log(e.message);
}

export default {
  initChat,
};
